import { useState } from "react";
import { post, del, useLive, type Settings } from "../lib/api";
import { Card, Button, Empty } from "../lib/ui";

const ROTULO_ESFORCO: Record<string, string> = {
  low: "baixo",
  medium: "médio",
  high: "alto",
  xhigh: "muito alto",
  max: "máximo",
};

/**
 * Conversar com a IA sem gastar um lead de verdade.
 *
 * Toda correção de prompt desta semana foi descoberta do mesmo jeito: alguém
 * real recebeu a mensagem ruim, e só depois a gente leu. O travessão, o
 * diminutivo, o "já anotei" — cada um custou uma conversa que não volta.
 *
 * Roda o caminho real: mesmo prompt, mesmas ferramentas, mesma base de
 * conhecimento, mesmo modelo. Cada turno custa dinheiro de verdade, e o custo
 * aparece na tela — testar é barato, mas não é grátis, e esconder isso faria
 * alguém deixar vinte turnos rodando sem perceber.
 */

interface Passo {
  nome: string;
  entrada: unknown;
  saida: string;
}

interface Resposta {
  baloes: string[];
  ferramentas: string[];
  passos: Passo[];
  pensamento: string | null;
  custo_usd: number;
  tokens: {
    input: number;
    cached: number;
    cacheWrite: number;
    output: number;
    chamadas: number;
  };
  fatos: { campo: string; valor: string }[];
}

/** O que um turno custou. Vai so no ultimo balao dele. */
interface Turno {
  custo: number;
  tokens: Resposta["tokens"];
}

interface Fala {
  de: "lead" | "ia";
  texto: string;
  /** Ferramentas do turno -- so no ultimo balao dele, para nao repetir. */
  passos?: Passo[];
  /**
   * Custo do turno. So no ultimo balao: antes ia em todos, e o total somava o
   * mesmo turno uma vez por balao -- resposta de dois baloes contava dobrado.
   */
  turno?: Turno;
  /** A IA escolheu nao responder (ex.: "ok" depois de marcado). */
  silencio?: boolean;
}

/*
 * Os campos que o formulário do Meta manda hoje.
 *
 * São estes porque é o que chega de verdade -- testar com um formulário que
 * ninguém usa testaria outra coisa.
 */
const CAMPOS = [
  { chave: "empreendimento_interesse", rotulo: "Empreendimento", exemplo: "Estoril" },
  { chave: "entrada_disponivel", rotulo: "Entrada (R$)", exemplo: "80000" },
  { chave: "parcela_mensal", rotulo: "Parcela (R$)", exemplo: "3000" },
  { chave: "regiao_interesse", rotulo: "Região", exemplo: "Tabuleiro" },
];

export function Simulador() {
  const { data: cfg } = useLive<Settings>("/settings", []);
  const [nome, setNome] = useState("Lead de teste");
  const [campos, setCampos] = useState<Record<string, string>>({
    empreendimento_interesse: "Estoril",
    entrada_disponivel: "80000",
    parcela_mensal: "3000",
  });
  const [leadId, setLeadId] = useState<string | null>(null);
  const [falas, setFalas] = useState<Fala[]>([]);
  const [texto, setTexto] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [fatos, setFatos] = useState<{ campo: string; valor: string }[]>([]);

  const gasto = falas.reduce((s, f) => s + (f.turno?.custo ?? 0), 0);

  async function turno(id: string, msg?: string) {
    setOcupado(true);
    setErro(null);
    try {
      const r = await post<Resposta>(`/simulacao/${id}/mensagem`, {
        texto: msg,
      });
      // Sem balao, a IA ficou em silencio de proposito -- e isso tambem e
      // resposta, precisa aparecer. As ferramentas vao so no ultimo balao.
      const turno: Turno = { custo: r.custo_usd, tokens: r.tokens };
      const novas: Fala[] =
        r.baloes.length === 0
          ? [{ de: "ia", texto: "", silencio: true, passos: r.passos, turno }]
          : r.baloes.map((b, i) => {
              const ultimo = i === r.baloes.length - 1;
              return {
                de: "ia" as const,
                texto: b,
                passos: ultimo ? r.passos : undefined,
                turno: ultimo ? turno : undefined,
              };
            });
      setFalas((f) => [...f, ...novas]);
      setFatos(r.fatos);
    } catch (e) {
      setErro((e as Error).message);
    }
    setOcupado(false);
  }

  async function iniciar() {
    setOcupado(true);
    setErro(null);
    setFalas([]);
    setFatos([]);
    try {
      const r = await post<{ leadId: string }>("/simulacao", {
        nome,
        respostas: campos,
      });
      setLeadId(r.leadId);
      await turno(r.leadId);
    } catch (e) {
      setErro((e as Error).message);
      setOcupado(false);
    }
  }

  async function enviar() {
    if (!leadId || !texto.trim()) return;
    const meu = texto.trim();
    setTexto("");
    setFalas((f) => [...f, { de: "lead", texto: meu }]);
    await turno(leadId, meu);
  }

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
      <div className="space-y-3">
        {!leadId ? (
          <Card className="p-5">
            <div className="text-sm font-semibold text-ink">Como o lead chegou</div>
            <p className="text-xs text-ink-3 mt-1 max-w-prose">
              Preencha como se fosse o formulário do anúncio. A IA vai abrir a
              conversa com esses dados, exatamente como faz em produção.
            </p>

            {/* Rotulo sempre visivel: so com placeholder, o campo preenchido
                vira "80000" solto, sem dizer se e entrada ou parcela. */}
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <label className="space-y-1.5 sm:col-span-2">
                <span className="text-xs font-medium text-ink-3">Nome</span>
                <input
                  className="w-full px-3 text-sm"
                  placeholder="Nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                />
              </label>
              {CAMPOS.map((c) => (
                <label key={c.chave} className="space-y-1.5">
                  <span className="text-xs font-medium text-ink-3">{c.rotulo}</span>
                  <input
                    className="w-full px-3 text-sm"
                    placeholder={`ex.: ${c.exemplo}`}
                    value={campos[c.chave] ?? ""}
                    onChange={(e) =>
                      setCampos({ ...campos, [c.chave]: e.target.value })
                    }
                  />
                </label>
              ))}
            </div>

            <Button variant="primary" className="mt-4" onClick={iniciar} disabled={ocupado}>
              {ocupado ? "escrevendo…" : "Começar conversa"}
            </Button>
          </Card>
        ) : (
          <Card className="p-4">
            <div className="space-y-3">
              {falas.map((f, i) => (
                <div
                  key={i}
                  className={f.de === "lead" ? "flex justify-end" : ""}
                >
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                      f.de === "lead"
                        ? "bg-accent/15 text-ink"
                        : "bg-surface-2 text-ink-2"
                    }`}
                  >
                    {f.silencio ? (
                      <span className="italic text-ink-3">
                        (não respondeu — ficou em silêncio de propósito)
                      </span>
                    ) : (
                      f.texto
                    )}
                    {f.turno && (
                      <details className="mt-1.5 group">
                        <summary className="text-[11px] text-ink-3 cursor-pointer select-none hover:text-ink-2 list-none">
                          {f.passos && f.passos.length > 0
                            ? `usou: ${f.passos.map((p) => p.nome).join(", ")} · `
                            : ""}
                          US$ {f.turno.custo.toFixed(4)} ·{" "}
                          {f.turno.tokens.chamadas}{" "}
                          {f.turno.tokens.chamadas === 1 ? "chamada" : "chamadas"}{" "}
                          <span className="text-accent group-open:hidden">· ver detalhes</span>
                        </summary>
                        <div className="mt-2 space-y-2">
                          {/* De onde saiu o custo deste turno. */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-ink-3 tabular-nums rounded-md bg-ground/60 border border-rule p-2">
                            <span>lido do cache</span>
                            <span className="text-right text-ink-2">{f.turno.tokens.cached.toLocaleString("pt-BR")}</span>
                            <span>gravado no cache</span>
                            <span className="text-right text-ink-2">{f.turno.tokens.cacheWrite.toLocaleString("pt-BR")}</span>
                            <span>entrada nova</span>
                            <span className="text-right text-ink-2">{f.turno.tokens.input.toLocaleString("pt-BR")}</span>
                            <span>saída (raciocínio + texto)</span>
                            <span className="text-right text-ink-2">{f.turno.tokens.output.toLocaleString("pt-BR")}</span>
                          </div>
                          {(f.passos ?? []).map((p, j) => (
                            <div key={j} className="rounded-md bg-ground/60 border border-rule p-2">
                              <div className="text-[11px] font-semibold text-warm">{p.nome}</div>
                              <pre className="text-[11px] text-ink-3 whitespace-pre-wrap font-mono mt-1">
                                {JSON.stringify(p.entrada, null, 2)}
                              </pre>
                              <p className="text-[11px] text-ink-2 whitespace-pre-wrap mt-1.5 pl-2 border-l-2 border-rule-2">
                                {p.saida}
                              </p>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              ))}
              {ocupado && (
                <div className="text-xs text-ink-3">a IA está escrevendo…</div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <input
                className="flex-1 bg-surface-2 border border-rule-2 rounded-lg px-3 py-1.5 text-sm text-ink"
                placeholder="responda como se fosse o lead…"
                value={texto}
                disabled={ocupado}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void enviar();
                }}
              />
              <Button variant="primary" onClick={enviar} disabled={ocupado}>
                Enviar
              </Button>
            </div>
          </Card>
        )}

        {erro && <p className="text-sm text-danger">{erro}</p>}
      </div>

      <div className="space-y-3">
        <Card className="p-4">
          <div className="text-xs font-medium text-ink-3">Custo desta conversa</div>
          <div className="text-2xl font-semibold tracking-tight text-ink tabular-nums mt-1">
            US$ {gasto.toFixed(4)}
          </div>
          {/* Com quem esta conversa roda. Sem isto, comparar Opus e Sonnet
              dependia de lembrar o que estava salvo em Ajustes na hora. */}
          {cfg && (
            <div className="text-xs text-ink-2 mt-2">
              {cfg.modelo === "claude-sonnet-5" ? "Sonnet 5" : "Opus 5"} · esforço{" "}
              {ROTULO_ESFORCO[cfg.effort] ?? cfg.effort}
            </div>
          )}
          <div className="text-[11px] text-ink-3 mt-1">
            É dinheiro de verdade: o simulador usa o modelo e o esforço salvos
            em Ajustes, os mesmos da produção.
          </div>
        </Card>

        {fatos.length > 0 && (
          <Card className="p-3">
            <div className="text-xs text-ink-3 mb-2">O que a IA registrou</div>
            <div className="space-y-1">
              {fatos.map((f) => (
                <div key={f.campo} className="text-sm">
                  <span className="text-ink-3">{f.campo}: </span>
                  <span className="text-ink-2">{f.valor}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="flex flex-col gap-2">
          {leadId && (
            <Button onClick={() => { setLeadId(null); setFalas([]); setFatos([]); }}>
              Nova conversa
            </Button>
          )}
          <Button
            variant="danger"
            onClick={async () => {
              await del<{ apagadas: number }>("/simulacao");
              setLeadId(null);
              setFalas([]);
              setFatos([]);
            }}
          >
            Apagar todas as simulações
          </Button>
        </div>

        <p className="text-xs text-ink-3">
          Simulações ficam fora da fila, do funil, da agenda e da Análise. O
          botão vermelho existe como rede: se algum filtro falhar, apagar tudo
          devolve os números ao lugar.
        </p>
      </div>
    </div>
  );
}
